import { useEffect, useState } from 'react';
import { Modal } from '../../../components';
import { MarketPlaceSpecimenResponse } from '../../../types/interfaces/marketPlace.interfaces';
import { Genders } from '../../../types/enums';
import { centsToDollars, dollarsToCents } from '../../../services/account.service';
import {
  BUTTON_CONSTANTS,
  MARKETPLACE_CONSTANTS,
  MARKETPLACE_LISTING_CONSTANTS,
  NAVIGATION_LINK_CONSTANTS,
} from '../../../constants/common';
import './editListingModal.scss';

export type EditListingModalProps = {
  isOpen: boolean;
  specimen: MarketPlaceSpecimenResponse | null;
  width?: number;
  onClose: () => void;
  onConfirm: (price: number, quantity: number) => void;
};

const isFemaleSpecimen = (specimen: MarketPlaceSpecimenResponse): boolean => {
  const gender = specimen.animal?.gender ?? specimen.specimen?.animal?.gender;
  return String(gender ?? '').toLowerCase() === Genders.Female.toLowerCase();
};

const EditListingModal = ({ isOpen, specimen, width = 700, onClose, onConfirm }: EditListingModalProps) => {
  const [price, setPrice] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(0);

  useEffect(() => {
    if (specimen) {
      setPrice(specimen.unitPriceInCents ? Number(centsToDollars(specimen.unitPriceInCents)) : 0);
      setQuantity(specimen.quantity ?? 0);
    }
  }, [specimen]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (!isNaN(val) && val >= 0) {
      setPrice(val);
    }
  };

  const handleConfirm = () => {
    onConfirm(dollarsToCents(price), quantity);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={width} ignoreBackdrop>
      <div className="edit-listing-modal">
        <div className="header">
          <h4>{MARKETPLACE_LISTING_CONSTANTS.EDIT_LISTING}</h4>
          <hr />
        </div>

        <div className="summary">
          <p className="summary__label">{NAVIGATION_LINK_CONSTANTS.LISTING_SUMMARY}</p>
          <h3 className="summary__name">
            {specimen && isFemaleSpecimen(specimen) ? specimen?.embryoStraw?.embryoStrawCode : specimen?.specimenId} -{' '}
            {specimen?.animal?.name}
          </h3>
          <hr />
          <div className="summary__grid">
            <div>
              <p className="summary__field-label">{MARKETPLACE_LISTING_CONSTANTS.BREED}</p>
              <p className="summary__field-value">{specimen?.animal?.breed?.name}</p>
            </div>
            <div>
              <p className="summary__field-label">{MARKETPLACE_CONSTANTS.PRODUCT_CODE}</p>
              <p className="summary__field-value">{specimen?.animal?.code}</p>
            </div>
            <div>
              <p className="summary__field-label">{MARKETPLACE_LISTING_CONSTANTS.CURRENT_PRICE}</p>
              <p className="summary__field-value">
                ${specimen?.unitPriceInCents ? (specimen.unitPriceInCents / 100).toFixed(2) : '0.00'} per unit
              </p>
            </div>
            <div>
              <p className="summary__field-label">{MARKETPLACE_LISTING_CONSTANTS.CURRENT_STOCK}</p>
              <p className="summary__field-value">{specimen?.quantity} Unit</p>
            </div>
          </div>
        </div>

        <div className="body">
          <h4>Edit Details</h4>
          <div className="form-group">
            <label>Price per unit*</label>
            <div className="input-wrapper">
              <span>$</span>
              <input type="number" min="0" value={price || ''} onChange={handlePriceChange} />
            </div>
          </div>
          <div className="form-group">
            <p className="hint">Maximum quantity cannot exceed available inventory stock.</p>
          </div>
        </div>

        <div className="footer">
          <button className="button green inverted medium" onClick={onClose}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button className="button green medium" onClick={handleConfirm}>
            {BUTTON_CONSTANTS.SAVE_CHANGES}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditListingModal;
